import { useEffect, useMemo, useState } from 'react'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { CursorPagination, type PageInfo } from '../ui'
import { PermissionDrawer } from '../drawers/PermissionDrawer'
import {
  UnifiedCreateUserModal,
  type GroupOption,
  type CompanyOption,
  type CreateUserInput,
  type CreateGroupOwnerInput,
  type CreateSuperAdminInput,
  type CreateComplianceOfficerInput,
} from '../modals/UnifiedCreateUserModal'
import { AddExistingGroupOwnerModal, type UserOption } from '../modals/AddExistingGroupOwnerModal'
import { AddExistingUserModal } from '../modals/AddExistingUserModal'
import { type UserData } from './UsersTabView'
import { Eye } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog'
import { formatDate } from '../../lib/transaction-utils'
import { SkeletonCards } from '../skeleton/Skeleton'
import { UserRoleBadge, UserStatusBadge } from '../badges/status'
import { UserRole } from '../../types/auth'
import { FallbackUserIcon } from '../assets/fallback'

export interface UsersTableProps {
  users: UserData[]
  loading?: boolean
  showViewPermissionsButton?: boolean
  showCreateButton?: boolean
  showAddExistingButton?: boolean
  showAllRolesInPermissions?: boolean
  allowedRoles?: UserRole[]
  onManageUser?: (user: UserData) => void

  // Context data
  groupId?: string
  companyId?: string
  adminOnly?: boolean

  // Data for modals
  groups?: GroupOption[]
  companies?: CompanyOption[]
  availableUsers?: UserOption[]
  usersLoading?: boolean

  // Callbacks
  onCreateUser?: (input: CreateUserInput) => Promise<void>
  onCreateGroupOwner?: (input: CreateGroupOwnerInput) => Promise<void>
  onCreateSuperAdmin?: (input: CreateSuperAdminInput) => Promise<void>
  onCreateComplianceOfficer?: (input: CreateComplianceOfficerInput) => Promise<void>
  onAddUserToGroup?: (userId: string, groupId: string, asAdmin: boolean) => Promise<void>
  onAddUserToCompany?: (userId: string, companyId: string) => Promise<void>
  onUserSearchChange?: (search: string) => void
  onValidationError?: (field: string, message: string) => void
  onUnassignUserFromGroup?: (userId: string, groupId: string) => Promise<void>
  onUnassignUserFromCompany?: (userId: string, companyId: string) => Promise<void>

  // Pagination props
  pageInfo?: PageInfo
  totalCount?: number
  onNextPage?: (cursor: string) => void
  onPreviousPage?: (cursor: string) => void
  showPagination?: boolean

  // Modal trigger handlers (exposed to parent)
  onModalHandlersReady?: (handlers: {
    handleViewPermissionsClick: () => void
    handleCreateClick: () => void
    handleAddExistingClick: () => void
  }) => void
}

export function UsersTable({
  users,
  loading = false,
  showViewPermissionsButton = false,
  showCreateButton = false,
  showAddExistingButton = false,
  showAllRolesInPermissions = false,
  onManageUser,
  groupId,
  companyId,
  adminOnly = false,
  groups = [],
  companies = [],
  availableUsers = [],
  usersLoading = false,
  onCreateUser,
  onCreateGroupOwner,
  onCreateSuperAdmin,
  onCreateComplianceOfficer,
  onAddUserToGroup,
  onAddUserToCompany,
  onUserSearchChange,
  onValidationError,
  onUnassignUserFromGroup,
  onUnassignUserFromCompany,
  pageInfo,
  totalCount = 0,
  onNextPage,
  onPreviousPage,
  showPagination = false,
  onModalHandlersReady,
  allowedRoles,
}: UsersTableProps) {
  // Modal states
  const [isPermissionDrawerOpen, setIsPermissionDrawerOpen] = useState(false)
  const [isUnassignUserFromGroupOpen, setIsUnassignUserFromGroupOpen] = useState(false)
  const [isUnassignUserFromCompanyOpen, setIsUnassignUserFromCompanyOpen] = useState(false)
  const [selectedUserToUnassign, setSelectedUserToUnassign] = useState<UserData | null>(null)
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false)
  const [isAddExistingGroupOwnerModalOpen, setIsAddExistingGroupOwnerModalOpen] = useState(false)
  const [isAddExistingUserModalOpen, setIsAddExistingUserModalOpen] = useState(false)

  // Handle external modal triggers from header
  const handleViewPermissionsClick = () => setIsPermissionDrawerOpen(true)
  const handleCreateClick = () => setIsCreateUserModalOpen(true)
  const handleAddExistingClick = () => {
    if (adminOnly) {
      console.log('adminOnly')
      setIsAddExistingGroupOwnerModalOpen(true)
    } else {
      setIsAddExistingUserModalOpen(true)
    }
  }

  const sortedUsers = useMemo(
    () => users.sort((a, b) => a.username.localeCompare(b.username)),
    [users],
  )

  // Expose handlers to parent on mount
  useEffect(() => {
    if (onModalHandlersReady) {
      onModalHandlersReady({
        handleViewPermissionsClick,
        handleCreateClick,
        handleAddExistingClick,
      })
    }
  }, [onModalHandlersReady])

  if (loading) {
    return <SkeletonCards />
  }

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {sortedUsers.map((user) => (
          <Card key={user.id} className="border-0 shadow-sm py-0">
            <CardContent className="flex items-center justify-between p-6 w-full">
              <div className="flex items-center gap-4 w-full">
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                    <div className="flex items-center gap-4 ">
                      <Avatar className="rounded-sm">
                        <AvatarFallback>
                          <FallbackUserIcon />
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex flex-col justify-center w-[200px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <h3 className="font-semibold text-lg truncate">{user.username}</h3>
                          </TooltipTrigger>
                          <TooltipContent>{user.username}</TooltipContent>
                        </Tooltip>
                        <div className="flex items-center gap-2">
                          <UserRoleBadge role={user.role} />

                          <UserStatusBadge
                            isWhitelisted={user.isWhitelisted}
                            isTrader={user.role === UserRole.Trader}
                            isActive={user.isActive}
                          />
                        </div>
                      </div>
                    </div>

                    {user.role !== 'SUPER_ADMIN' && user.role !== 'COMPLIANCE' ? (
                      <div className="flex gap-8">
                        <div className="flex items-center gap-2 md:block">
                          <p className="text-sm text-muted-foreground leading-none">
                            Assigned Companies
                          </p>
                          <p className="text-lg font-black leading-none">{user.companiesCount}</p>
                        </div>
                        <div className="flex items-center gap-2 md:block">
                          <p className="text-sm text-muted-foreground leading-none">
                            Assigned Groups
                          </p>
                          <p className="text-lg font-black leading-none">{user.groupsCount}</p>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="flex justify-between md:flex-row flex-col gap-4 md:gap-0">
                    <div className="flex flex-col">
                      <p className="text-sm">{user.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Last Active: {formatDate(user.lastActive)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {onUnassignUserFromGroup && user.role === UserRole.GroupOwner && (
                        <Button
                          variant="destructive"
                          className="w-[150px] rounded-full"
                          onClick={() => {
                            setIsUnassignUserFromGroupOpen(true)
                            setSelectedUserToUnassign(user)
                          }}
                        >
                          Unassign
                        </Button>
                      )}

                      {onUnassignUserFromCompany && user.role === UserRole.Trader && (
                        <Button
                          variant="destructive"
                          className="w-[150px] rounded-full"
                          onClick={() => {
                            setIsUnassignUserFromCompanyOpen(true)
                            setSelectedUserToUnassign(user)
                          }}
                        >
                          Unassign
                        </Button>
                      )}

                      {onManageUser && (
                        <Button
                          variant="main_action"
                          className="w-[150px]"
                          onClick={() => onManageUser(user)}
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {showPagination && pageInfo && onNextPage && onPreviousPage && (
        <div className="mt-4">
          <CursorPagination
            pageInfo={pageInfo}
            totalCount={totalCount}
            loading={loading}
            onNext={onNextPage}
            onPrevious={onPreviousPage}
          />
        </div>
      )}

      {/* Modals and Dialogs */}
      {onUnassignUserFromGroup && (
        <ConfirmationDialog
          open={isUnassignUserFromGroupOpen}
          onOpenChange={setIsUnassignUserFromGroupOpen}
          title="Confirm Unassignment"
          description={`Are you sure you want to unassign this user from the group "${groups.find((group) => group.groupId === groupId)?.name}"? This action cannot be undone and will remove the user's access to this group.`}
          confirmText="Unassign"
          cancelText="Cancel"
          onConfirm={() => {
            if (selectedUserToUnassign && groupId) {
              onUnassignUserFromGroup(selectedUserToUnassign.id, groupId)
              setIsUnassignUserFromGroupOpen(false)
            }
          }}
        />
      )}

      {onUnassignUserFromCompany && (
        <ConfirmationDialog
          open={isUnassignUserFromCompanyOpen}
          onOpenChange={setIsUnassignUserFromCompanyOpen}
          title="Confirm Unassignment"
          description={`Are you sure you want to unassign this user from the company "${companies.find((company) => company.id === companyId)?.company}"? This action cannot be undone and will remove the user's access to this company.`}
          confirmText="Unassign"
          cancelText="Cancel"
          onConfirm={() => {
            if (selectedUserToUnassign && companyId) {
              onUnassignUserFromCompany(selectedUserToUnassign.id, companyId)
              setIsUnassignUserFromCompanyOpen(false)
            }
          }}
        />
      )}

      {showViewPermissionsButton && (
        <PermissionDrawer
          open={isPermissionDrawerOpen}
          onOpenChange={setIsPermissionDrawerOpen}
          showAllRoles={showAllRolesInPermissions}
        />
      )}

      {showCreateButton &&
        (onCreateUser || onCreateGroupOwner || onCreateSuperAdmin || onCreateComplianceOfficer) && (
          <UnifiedCreateUserModal
            open={isCreateUserModalOpen}
            onOpenChange={setIsCreateUserModalOpen}
            groupId={groupId}
            companyId={companyId}
            groups={groups}
            companies={companies}
            loading={loading}
            onCreateUser={onCreateUser}
            onCreateGroupOwner={onCreateGroupOwner}
            onCreateSuperAdmin={onCreateSuperAdmin}
            onCreateComplianceOfficer={onCreateComplianceOfficer}
            onValidationError={onValidationError}
            allowedRoles={allowedRoles}
          />
        )}

      {groupId && showAddExistingButton && onAddUserToGroup && (
        <>
          <AddExistingGroupOwnerModal
            open={isAddExistingGroupOwnerModalOpen}
            onOpenChange={setIsAddExistingGroupOwnerModalOpen}
            groupId={groupId}
            users={availableUsers}
            loading={usersLoading}
            onAddUser={onAddUserToGroup}
            onSearchChange={onUserSearchChange}
          />
          <AddExistingUserModal
            open={isAddExistingUserModalOpen}
            onOpenChange={setIsAddExistingUserModalOpen}
            groupId={groupId}
            companyId={companyId}
            users={availableUsers}
            loading={usersLoading}
            onAddUserToGroup={onAddUserToGroup}
            onAddUserToCompany={onAddUserToCompany}
            onSearchChange={onUserSearchChange}
          />
        </>
      )}
    </div>
  )
}
